import { h } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { memo } from 'preact/compat';

import { useSelector, useDispatch } from 'react-redux';

import { styled } from '@mui/material/styles';

import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Skeleton from '@mui/material/Skeleton';
import Collapse from '@mui/material/Collapse';

import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';

import { actions } from '../../store/state/drawer.js';

import ReactiveItemLink from './reactiveItemLink.jsx';
import toggleBoolean from '../../utils/toggleBoolean.js';
import OrganizationList from './organizationList.jsx';
import { getLogoutUrl } from '../../oryIntegration/index.js';
import globalErrorHandler from '../../utils/errorHandler.js';
import OnRpm from '../conditionals/onRpm.jsx';
import OnRunner from '../conditionals/onRunner.jsx';
import { ON_RPM, RM_DRAWER_MENU_ITEMS } from '../../consts.js';
import IsRebelmouseStaff from '../conditionals/isRebelMouseStaff.jsx';

const DRAWER_WIDTH = 240;

function getDrawerState(state) {
  return state.drawer;
}

const Drawer = styled(
  MuiDrawer,
  {}
)(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    whiteSpace: 'nowrap',
    width: DRAWER_WIDTH,
    maxWidth: '100vw',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
  },
  ...(!open && {
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9),
    },
  }),
}));

function renderOrganizations() {
  return <OrganizationList />;
}

function renderRebelmouseMenu(menuItem) {
  const { caption, href } = menuItem;
  return (
    <ListItemButton component={'a'} href={href}>
      <ListItemText>{caption}</ListItemText>
    </ListItemButton>
  );
}

function Sidebar(_props) {
  const dispatch = useDispatch();
  const open = useSelector(getDrawerState);
  const [shouldFetchLogout, setShouldFetchLogout] = useState(false);
  const [logoutUrl, setLogoutUrl] = useState(null);

  const [isOrganizationListExpanded, setOrganizationListExpanded] =
    useState(true);

  const closeDrawer = useCallback(
    function closeDrawer() {
      dispatch(actions.CLOSE());
    },
    [dispatch]
  );

  const toggleOrganizationList = useCallback(function toggleOrganizationList() {
    setOrganizationListExpanded(toggleBoolean);
  }, []);

  const organizationList = useMemo(renderOrganizations, []);

  useEffect(
    function onOpen() {
      if (open && ON_RPM) {
        setShouldFetchLogout(true);
      }
    },
    [open]
  );

  useEffect(
    function fetchLogoutUrl() {
      if (!shouldFetchLogout) {
        return;
      }

      getLogoutUrl().then(setLogoutUrl).catch(globalErrorHandler);
    },
    [shouldFetchLogout]
  );

  return (
    <Drawer
      open={open}
      onClose={closeDrawer}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <List component="nav">
        <OnRpm>
          <ReactiveItemLink href="/properties">
            <ListItemText primary="All my properties" />
          </ReactiveItemLink>

          <Divider sx={{ my: 1 }} />

          <ListItemButton onClick={toggleOrganizationList}>
            <ListItemText primary="Organizations" />
            {isOrganizationListExpanded ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={isOrganizationListExpanded}>
            <List component="div" disablePadding>
              {organizationList}
            </List>
          </Collapse>

          <Divider sx={{ my: 1 }} />
          <IsRebelmouseStaff>
            <ReactiveItemLink href="/adminUserList">
              <ListItemText primary="User list" />
            </ReactiveItemLink>
          </IsRebelmouseStaff>
          <Divider sx={{ my: 1 }} />

          <ListItemButton
            component={logoutUrl ? 'a' : void 0}
            href={logoutUrl || '#'}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText>
              {logoutUrl ? 'Logout' : <Skeleton width={100} />}
            </ListItemText>
          </ListItemButton>
        </OnRpm>
        <OnRunner>
          {RM_DRAWER_MENU_ITEMS.map(renderRebelmouseMenu)}
          <Divider />
        </OnRunner>
      </List>
    </Drawer>
  );
}

export default memo(Sidebar);
